import { apiFetch } from './api-client';

export type ChatRole = 'user' | 'assistant';
export type ChatLanguage = 'en' | 'hi' | 'ta' | 'te';

export interface ChatCard {
    type: string;
    [key: string]: unknown;
}

export interface ChatMessage {
    id: string;
    role: ChatRole;
    content: string;
    ts: number;
    intent?: string;
    cards?: ChatCard[];
}

export interface ChatSession {
    id: string;
    title: string;
    language: ChatLanguage;
    messages: ChatMessage[];
    updatedAt: number;
}

export interface ChatStreamResult {
    message: string;
    intent?: string;
    cards?: ChatCard[];
    suggestions?: string[];
    language?: string;
}

const STORAGE_KEY = 'imm-chat-sessions-v1';
const MAX_SESSIONS = 8;
const MAX_MESSAGES = 40;
const CONTEXT_RECENT_MESSAGES = 20;

function normalizeLanguage(language: string): ChatLanguage {
    if (language === 'hi' || language === 'ta' || language === 'te') return language;
    return 'en';
}

function makeId(prefix: string): string {
    return `${prefix}-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

export function newSession(language: ChatSession['language'] = 'en'): ChatSession {
    return {
        id: makeId('session'),
        title: 'New Chat',
        language: normalizeLanguage(language),
        messages: [],
        updatedAt: Date.now(),
    };
}

export function loadSessions(): ChatSession[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as ChatSession[];
        if (!Array.isArray(parsed)) return [];
        return parsed.map((session) => ({
            ...session,
            language: normalizeLanguage(session.language),
            messages: Array.isArray(session.messages) ? session.messages : [],
            updatedAt: Number(session.updatedAt || Date.now()),
        }));
    } catch {
        return [];
    }
}

export function saveSessions(sessions: ChatSession[]): void {
    const trimmed = sessions
        .slice(0, MAX_SESSIONS)
        .map(session => ({
            ...session,
            messages: session.messages.slice(-MAX_MESSAGES),
            updatedAt: Date.now(),
        }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
}

export function upsertSession(next: ChatSession): ChatSession[] {
    const sessions = loadSessions();
    const rest = sessions.filter(item => item.id !== next.id);
    const merged = [{ ...next, updatedAt: Date.now() }, ...rest]
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .slice(0, MAX_SESSIONS);
    saveSessions(merged);
    return merged;
}

export function addMessage(session: ChatSession, message: Omit<ChatMessage, 'id' | 'ts'>): ChatSession {
    const nextMessage: ChatMessage = {
        ...message,
        id: makeId('msg'),
        ts: Date.now(),
    };

    const messages = [...session.messages, nextMessage].slice(-MAX_MESSAGES);
    const title = session.title === 'New Chat' && message.role === 'user'
        ? message.content.slice(0, 42)
        : session.title;

    return {
        ...session,
        title,
        messages,
        updatedAt: Date.now(),
    };
}

export async function fetchChatSuggestions(): Promise<string[]> {
    try {
        const result = await apiFetch<{ suggestions?: string[] }>('/llm/chat', {
            cacheTTL: 180,
            staleWhileRevalidate: true,
        });
        return Array.isArray(result.data.suggestions) ? result.data.suggestions.slice(0, 6) : [];
    } catch {
        return [];
    }
}

export async function sendChatMessage(args: {
    message: string;
    language: ChatSession['language'];
    messages: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>;
}): Promise<{ message: string; intent?: string; cards?: ChatCard[]; suggestions?: string[] }> {
    const response = await fetch('/api/llm/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(args),
    });

    if (!response.ok) {
        throw new Error(`Chat request failed with ${response.status}`);
    }

    const payload = await response.json() as {
        message?: string;
        intent?: string;
        cards?: ChatCard[];
        suggestions?: string[];
    };

    return {
        message: payload.message || 'No response available.',
        intent: payload.intent,
        cards: Array.isArray(payload.cards) ? payload.cards : [],
        suggestions: Array.isArray(payload.suggestions) ? payload.suggestions : [],
    };
}

function compactText(value: string): string {
    return value.replace(/\s+/g, ' ').trim();
}

function summarizeWindow(messages: ChatMessage[]): string {
    if (messages.length === 0) return '';

    const userPrompts = messages.filter((item) => item.role === 'user').map((item) => compactText(item.content)).filter(Boolean);
    const assistantReplies = messages.filter((item) => item.role === 'assistant').map((item) => compactText(item.content)).filter(Boolean);
    const promptHead = userPrompts.slice(-5).join(' | ').slice(0, 260);
    const answerHead = assistantReplies.slice(-3).join(' | ').slice(0, 260);

    return [
        `Earlier turns compressed (${messages.length} messages).`,
        promptHead ? `User focus: ${promptHead}.` : '',
        answerHead ? `Assistant summary: ${answerHead}.` : '',
    ].filter(Boolean).join(' ');
}

export function buildChatContext(messages: ChatMessage[]): Array<{ role: 'user' | 'assistant' | 'system'; content: string }> {
    if (messages.length <= CONTEXT_RECENT_MESSAGES) {
        return messages.map((item) => ({ role: item.role, content: item.content }));
    }

    const older = messages.slice(0, -CONTEXT_RECENT_MESSAGES);
    const recent = messages.slice(-CONTEXT_RECENT_MESSAGES);
    const compressed = summarizeWindow(older);

    const systemMessage = compressed
        ? [{ role: 'system' as const, content: compressed }]
        : [];

    return [
        ...systemMessage,
        ...recent.map((item) => ({ role: item.role, content: item.content })),
    ];
}

function parseSSEChunk(chunk: string): Array<{ event: string; data: string }> {
    const blocks = chunk.split('\n\n').map(block => block.trim()).filter(Boolean);
    const events: Array<{ event: string; data: string }> = [];

    for (const block of blocks) {
        const lines = block.split('\n');
        let event = 'message';
        let data = '';
        for (const line of lines) {
            if (line.startsWith('event:')) {
                event = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
                data = line.slice(5).trim();
            }
        }
        if (data) events.push({ event, data });
    }

    return events;
}

export async function sendChatMessageStream(args: {
    message: string;
    language: ChatSession['language'];
    messages: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>;
    onToken?: (token: string) => void;
    onMeta?: (meta: Omit<ChatStreamResult, 'message'>) => void;
}): Promise<ChatStreamResult> {
    const response = await fetch('/api/llm/chat-stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            message: args.message,
            language: args.language,
            messages: args.messages,
        }),
    });

    if (!response.ok || !response.body) {
        throw new Error(`Chat stream failed with ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalPayload: ChatStreamResult = { message: '' };

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';

        for (const part of parts) {
            for (const evt of parseSSEChunk(`${part}\n\n`)) {
                if (evt.event === 'meta') {
                    const meta = JSON.parse(evt.data) as Omit<ChatStreamResult, 'message'>;
                    args.onMeta?.(meta);
                } else if (evt.event === 'token') {
                    const tokenPayload = JSON.parse(evt.data) as { token?: string };
                    const token = tokenPayload.token || '';
                    if (token) {
                        finalPayload.message += token;
                        args.onToken?.(token);
                    }
                } else if (evt.event === 'done') {
                    const donePayload = JSON.parse(evt.data) as ChatStreamResult;
                    finalPayload = {
                        ...donePayload,
                        message: donePayload.message || finalPayload.message,
                    };
                } else if (evt.event === 'error') {
                    const errorPayload = JSON.parse(evt.data) as { message?: string };
                    throw new Error(errorPayload.message || 'Chat stream failed');
                }
            }
        }
    }

    return finalPayload;
}

export async function submitChatFeedback(messageId: string, rating: 'up' | 'down', reason?: string): Promise<void> {
    await fetch('/api/llm/chat-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messageId, rating, reason: reason || '' }),
    });
}
